// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toApiError } from "@/api/api-error";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

type UsersSortBy = components["schemas"]["UsersSortByEnum"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

export interface UserRow {
  userId: number;
  publicId: string;
  email: string;
  userName?: string;
  avatar?: string;
  fullName?: string;
  isActive: boolean;
  isDeleted: boolean;
  hasMfa: boolean;
  realm: string;
  defaultStoreId?: number;
  lastLoginAt?: string;
  lastAttemptAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface RelevantStore {
  storeId: number;
  storeName: string;
  legibleName: string;
  timeZone: string;
}

export interface RelevantChain {
  chainId: number;
  chainName: string;
  legibleName: string;
  defaultTimeZone: string;
}

interface UsersListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: UsersSortBy;
  orderBy?: SortOrder;
  onlyActive?: boolean;
  realm?: string;
  chainName?: string;
  storeName?: string;
  group?: string;
}

export function useUsersQuery(params: UsersListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, onlyActive, realm, chainName, storeName, group } = params;

  return useQuery({
    queryKey: [
      "users",
      "list",
      { page, limit, search, sortBy, orderBy, onlyActive, realm, chainName, storeName, group },
    ],
    queryFn: async () => {
      const res = await api.GET("/v1/users", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[onlyActive]": onlyActive,
            "filter[realm]": realm || undefined,
            "filter[chainName]": chainName || undefined,
            "filter[storeName]": storeName || undefined,
            "filter[group]": group || undefined,
          },
        },
      });
      return res.data as unknown as {
        users: UserRow[];
        chains: RelevantChain[];
        stores: RelevantStore[];
        count: number;
      };
    },
  });
}

// --- Create ---

export interface CreateUserInput {
  email: string;
  userName?: string;
  fullName: string;
  realm: string;
  password?: string;
  groups: string[];
  defaultStoreId: string;
  /** User-group flags to stamp on the new user (e.g. `ft_fue_training_required`). */
  flags?: components["schemas"]["UserFlagEnum"][];
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const res = await api.POST("/v1/users", {
        body: {
          email: input.email,
          userName: input.userName || undefined,
          fullName: input.fullName,
          realm: input.realm,
          password: input.password || undefined,
          groups: input.groups,
          defaultStoreId: input.defaultStoreId,
          flags: input.flags,
        },
      });
      if (res.error) throw toApiError(res.error, res.response, "Failed to create user");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// --- Edit ---

// `supervisorUserId: null` clears the supervisor. The backend field is `int | None`, but the
// framework renders every Optional as a non-nullable type in the schema, so we widen it here.
type UserEditBody = Omit<components["schemas"]["UsersEditRequest"], "supervisorUserId"> & {
  supervisorUserId?: number | null;
};

export function useUpdateUserMutation(publicId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserEditBody) => {
      const res = await api.PATCH("/v1/users/{public_id}", {
        params: { path: { public_id: publicId } },
        body: body as components["schemas"]["UsersEditRequest"],
      });
      if (res.error) throw new Error("Failed to update user");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// --- Change default store ---

export function useChangeUserStoreMutation(publicId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { defaultStoreId: string; removePreviousDefault: boolean }) => {
      const res = await api.PATCH("/v1/users/{public_id}/store", {
        params: { path: { public_id: publicId } },
        body,
      });
      if (res.error) throw new Error("Failed to change store");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// --- Activate / Deactivate ---

export function useToggleUserActiveMutation(publicId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (isActive: boolean) => {
      const res = await api.PATCH("/v1/users/{public_id}/active", {
        params: { path: { public_id: publicId } },
        body: { isActive },
      });
      if (res.error) throw new Error("Failed to update");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useDeleteUserMutation(publicId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.DELETE("/v1/users/{public_id}", {
        params: { path: { public_id: publicId } },
      });
      if (res.error) throw new Error("Failed to delete");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export interface UserPermissionSource {
  groupName: string | null;
  permissions: string[];
}

export interface UserDetail {
  user: UserRow;
  groups: string[];
  defaultStore?: RelevantStore;
  defaultChain?: RelevantChain;
  availableStores: RelevantStore[];
  permissions: UserPermissionSource[];
  flags: components["schemas"]["UserFlagEnum"][];
  supervisor?: components["schemas"]["UserRelevantResponse"] | null;
}

// Build the base edit body from a loaded detail, preserving the user's current profile and store
// assignments. Spread overrides (e.g. `{ permissions }`) on top. Groups and password are omitted so
// they stay unchanged. `availableStoreIds` MUST be sent — it defaults to `[]` server-side, so
// omitting it would clear the user's available stores.
export function buildUserEditBaseBody(detail: UserDetail): components["schemas"]["UsersEditRequest"] {
  return {
    email: detail.user.email,
    userName: detail.user.userName || undefined,
    fullName: detail.user.fullName ?? "",
    realm: detail.user.realm,
    defaultStoreId: detail.defaultStore?.storeName,
    availableStoreIds: detail.availableStores.map((store) => store.storeName),
  };
}

/**
 * Fetch active users belonging to any of the given groups — used to populate the supervisor
 * selector. Unpaginated (`limit: -1`); disabled when no groups are requested.
 */
export function useUsersByGroupsQuery(groups: string[], enabled = true) {
  return useQuery({
    queryKey: ["users", "byGroups", groups],
    enabled: enabled && groups.length > 0,
    queryFn: async () => {
      const res = await api.GET("/v1/users", {
        params: { query: { limit: -1, "filter[groups]": groups, "filter[onlyActive]": true } },
      });
      return (res.data as unknown as { users: UserRow[] }).users;
    },
  });
}

type UserAlertsSortBy = components["schemas"]["UserAlertsSortByEnum"];
export type UserAlertSource = components["schemas"]["UserAlertSourceEnum"];

export interface UserAlertRow {
  alert: string;
  receives: boolean;
  /** How the user receives it — `inherited` means a subscription on a dotted ancestor covers it. */
  source?: UserAlertSource;
  /** The ancestor alert the subscription comes from (`inherited` only). */
  sourceAlert?: string;
  /** The user's own subscription flag — absent when they have no row for this alert at all. */
  subscriptionActive?: boolean;
  subscribedAt?: string;
}

interface UserAlertsParams {
  publicId: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: UserAlertsSortBy;
  orderBy?: SortOrder;
  onlyReceived?: boolean;
  source?: UserAlertSource | "";
}

/**
 * Every alert `messaging_alert_receivers` knows about, annotated with whether this user
 * receives it. Read-only — subscriptions aren't managed from the backoffice yet.
 */
export function useUserAlertsQuery({
  publicId,
  page = 1,
  limit = 20,
  search,
  sortBy,
  orderBy,
  onlyReceived,
  source,
}: UserAlertsParams) {
  return useQuery({
    queryKey: ["users", "alerts", publicId, { page, limit, search, sortBy, orderBy, onlyReceived, source }],
    queryFn: async () => {
      const res = await api.GET("/v1/users/{public_id}/alerts", {
        params: {
          path: { public_id: publicId },
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[onlyReceived]": onlyReceived,
            "filter[source]": source || undefined,
          },
        },
      });
      if (res.error) throw new Error("Failed to load user alerts");
      return res.data as unknown as { alerts: UserAlertRow[]; count: number };
    },
    enabled: !!publicId,
  });
}

/**
 * Subscribe the user to an alert or unsubscribe them from it. Idempotent, and it doubles as the
 * create path: subscribing to a name nothing has ever fired on is what gives that alert its first
 * row. Unsubscribing only ever touches the user's own row — an alert reaching them through an
 * ancestor subscription has to be dealt with on the ancestor.
 */
export function useSetUserAlertSubscriptionMutation(publicId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ alert, isActive }: { alert: string; isActive: boolean }) => {
      const res = await api.PATCH("/v1/users/{public_id}/alerts", {
        params: { path: { public_id: publicId } },
        body: { alert, isActive },
      });
      if (res.error) throw toApiError(res.error, res.response, "Failed to update alert subscription");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "alerts", publicId] });
    },
  });
}

export function useUserByPublicIdQuery(publicId: string) {
  return useQuery({
    queryKey: ["users", "detail", publicId],
    queryFn: async () => {
      const res = await api.GET("/v1/users/{public_id}", {
        params: { path: { public_id: publicId } },
      });
      if (res.error) throw new Error("Failed to load user");
      return res.data as unknown as UserDetail;
    },
    enabled: !!publicId,
  });
}
